import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const { theme, setLightTheme, setDarkTheme } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    subscriptionType: user?.subscriptionType || 'EARLY_BIRD'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      updateUser(formData);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: user?.fullName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      subscriptionType: user?.subscriptionType || 'EARLY_BIRD'
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const getSubscriptionBadge = (type) => {
    switch (type) {
      case 'EARLY_BIRD': return 'early-bird-badge';
      case 'MANAGED': return 'managed-badge';
      default: return 'basic-badge';
    }
  };

  const getSubscriptionPrice = (type) => {
    switch (type) {
      case 'EARLY_BIRD': return '$10/month (BYOK)';
      case 'MANAGED': return '$19/month (Managed)';
      default: return 'Free';
    }
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="error-message">
          Please log in to view your profile.
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2>Profile Settings</h2>
        <button 
          className="edit-button"
          onClick={() => setIsEditing(true)}
        >
          Edit Profile
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="profile-content">
        <div className="profile-info">
          <div className="profile-section">
            <h3>Account Information</h3>
            
            <div className="info-grid">
              <div className="info-item">
                <label>Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="profile-input"
                  />
                ) : (
                  <span className="info-value">{user.fullName}</span>
                )}
              </div>

              <div className="info-item">
                <label>Email</label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="profile-input"
                  />
                ) : (
                  <span className="info-value">{user.email}</span>
                )}
              </div>

              <div className="info-item">
                <label>Phone</label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="profile-input"
                  />
                ) : (
                  <span className="info-value">{user.phone}</span>
                )}
              </div>

              <div className="info-item">
                <label>Role</label>
                <span className={`role-badge ${user.role === 'ADMIN' ? 'admin-badge' : 'user-badge'}`}>
                  {user.role}
                </span>
              </div>

              <div className="info-item">
                <label>Subscription</label>
                {isEditing ? (
                  <select
                    name="subscriptionType"
                    value={formData.subscriptionType}
                    onChange={handleChange}
                    className="profile-select"
                  >
                    <option value="EARLY_BIRD">Early Bird ($10/month)</option>
                    <option value="MANAGED">Managed ($19/month)</option>
                  </select>
                ) : (
                  <span className={`subscription-badge ${getSubscriptionBadge(user.subscriptionType)}`}>
                    {user.subscriptionType.replace('_', ' ')}
                  </span>
                )}
              </div>

              <div className="info-item">
                <label>Member Since</label>
                <span className="info-value">
                  {new Date(user.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h3>Subscription Details</h3>
            <div className="subscription-info">
              <div className="subscription-card">
                <h4>Current Plan</h4>
                <p className="plan-name">{user.subscriptionType.replace('_', ' ')}</p>
                <p className="plan-price">{getSubscriptionPrice(user.subscriptionType)}</p>
                <div className="plan-features">
                  {user.subscriptionType === 'EARLY_BIRD' ? (
                    <>
                      <p>✅ Bring Your Own API Key</p>
                      <p>✅ Unlimited news feeds</p>
                      <p>✅ AI-powered analysis</p>
                      <p>✅ Custom prompts</p>
                    </>
                  ) : (
                    <>
                      <p>✅ Managed API access</p>
                      <p>✅ Priority support</p>
                      <p>✅ Advanced analytics</p>
                      <p>✅ Managed security</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Theme Settings */}
        <div className="profile-section">
          <div className="profile-section-header">
            <h3>Theme Preferences</h3>
          </div>
          <div className="theme-controls">
            <div className="theme-option">
              <label>
                <input 
                  type="radio" 
                  name="theme" 
                  value="light"
                  checked={theme === 'light'}
                  onChange={() => setLightTheme()}
                  className="theme-radio"
                />
                <div className="theme-preview light-theme">
                  <div className="theme-preview-header"></div>
                  <div className="theme-preview-content"></div>
                </div>
                <span className="theme-label">Light Mode</span>
              </label>
            </div>
            <div className="theme-option">
              <label>
                <input 
                  type="radio" 
                  name="theme" 
                  value="dark"
                  checked={theme === 'dark'}
                  onChange={() => setDarkTheme()}
                  className="theme-radio"
                />
                <div className="theme-preview dark-theme">
                  <div className="theme-preview-header"></div>
                  <div className="theme-preview-content"></div>
                </div>
                <span className="theme-label">Dark Mode</span>
              </label>
            </div>
          </div>
          <p className="theme-description">
            Choose your preferred theme. Changes take effect immediately.
          </p>
        </div>

        {isEditing && (
          <div className="profile-actions">
            <button 
              className="cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button 
              className="save-button"
              onClick={handleSave}
            >
              Save Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
